import { useState, useRef, useEffect, useMemo } from 'react';
import { Box } from '@mantine/core';
import { Task, TaskComment } from '../../../../types';
import { useTaskActions } from '../../../../hooks';
import { useLocalizedDate } from '../../../../hooks/useLocalizedDate';
import { useGeoMarkupStore } from '../../../../store';
import { commentsService } from '../../../../services/api';
import { TaskCommentsSignalR } from './TaskCommentsSignalR';
import {
  CommentInput,
  MessagesList,
  ScrollToBottomButton,
} from './refactored/components';
import { SCROLL_NEAR_BOTTOM_THRESHOLD } from './refactored/constants';
import { isNearBottom } from './refactored/utils';
import { mapCommentFromApi } from '@/store/mappers/commentMapper';
import styles from './TaskComments.module.css';

interface TaskCommentsProps {
  task: Task;
  readOnly?: boolean;
}

export const TaskComments = ({ task, readOnly = false }: TaskCommentsProps) => {
  const { receiveComment, updateComment, deleteComment } = useTaskActions();
  const { formatCommentTimestamp } = useLocalizedDate();
  const [newMessage, setNewMessage] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const refs = useRef({
    messagesContainer: null as HTMLDivElement | null,
    messagesEnd: null as HTMLDivElement | null,
  });

  const currentUserId = useGeoMarkupStore((state) => state.auth.user?.id ?? '');

  // --- Send / Edit / Delete via API ---
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    if (editingComment) {
      handleSaveEdit();
      return;
    }

    const message = newMessage.trim();
    setNewMessage('');

    try {
      const response = await commentsService.add(task.id, { message });
      receiveComment(task.id, mapCommentFromApi(response.data.comment));
    } catch (error) {
      console.error('Failed to send comment:', error);
      setNewMessage(message);
    }
  };

  const handleStartEdit = (comment: TaskComment) => {
    setEditingComment(comment.id);
    setNewMessage(comment.message);
  };

  const handleSaveEdit = async () => {
    if (!newMessage.trim() || !editingComment) return;

    const message = newMessage.trim();
    const commentId = editingComment;
    setEditingComment(null);
    setNewMessage('');

    try {
      const response = await commentsService.update(task.id, commentId, {
        message,
      });
      const mapped = mapCommentFromApi(response.data.comment);
      updateComment(task.id, commentId, mapped);
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setNewMessage('');
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentsService.delete(task.id, commentId);
      deleteComment(task.id, commentId);
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleCopyMessage = (message: string) => {
    navigator.clipboard.writeText(message);
  };

  const scrollToBottom = () => {
    refs.current.messagesEnd?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!refs.current.messagesContainer) return;

    const nearBottom = isNearBottom(
      refs.current.messagesContainer,
      SCROLL_NEAR_BOTTOM_THRESHOLD
    );
    setShowScrollButton(!nearBottom);
  };

  // Auto-scroll to bottom when TaskComments component mounts (when switching to comments tab)
  useEffect(() => {
    scrollToBottom();
  }, []);

  const comments = useMemo(
    () =>
      [...(task.comments || [])].sort(
        (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
      ),
    [task.comments]
  );
  const commentsLength = comments.length;

  // Auto-scroll to bottom when new message is added
  useEffect(() => {
    if (!refs.current.messagesContainer) return;

    const wasNearBottom = isNearBottom(
      refs.current.messagesContainer,
      SCROLL_NEAR_BOTTOM_THRESHOLD
    );
    if (wasNearBottom) {
      scrollToBottom();
    }
  }, [commentsLength]);

  // Scroll to edited message when edit mode starts
  useEffect(() => {
    if (editingComment && refs.current.messagesContainer) {
      const editedMessage = refs.current.messagesContainer.querySelector(
        `[data-comment-id="${editingComment}"]`
      );
      if (editedMessage) {
        editedMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [editingComment]);

  return (
    <Box className={styles.container}>
      {!readOnly && <TaskCommentsSignalR taskId={task.id} />}

      {/* Messages List */}
      <MessagesList
        comments={comments}
        currentUser={currentUserId}
        readOnly={readOnly}
        editingCommentId={editingComment}
        refs={refs}
        onScroll={handleScroll}
        onEdit={handleStartEdit}
        onDelete={handleDeleteComment}
        onCopy={handleCopyMessage}
        formatDate={formatCommentTimestamp}
      />

      {/* Scroll to bottom button - positioned above CommentInput */}
      <ScrollToBottomButton
        show={showScrollButton && !editingComment}
        onClick={scrollToBottom}
        className={styles.scrollButton}
      />

      {/* Message Input */}
      {!readOnly && (
        <CommentInput
          value={newMessage}
          onChange={setNewMessage}
          onSend={handleSendMessage}
          onCancel={handleCancelEdit}
          isEditing={!!editingComment}
        />
      )}
    </Box>
  );
};
